import { theme } from '../../../theme';
import type { DailyCount } from '../types/dashboard.types';

type ActivityChartProps = {
  dailyCounts: DailyCount[];
};

const BAR_HEIGHT_PX = 144;
const MIN_BAR_PX = 3;

export const ActivityChart = ({ dailyCounts }: ActivityChartProps) => {
  const maxCount = Math.max(...dailyCounts.map((d) => d.count), 1);
  const totalAssessments = dailyCounts.reduce((sum, d) => sum + d.count, 0);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  // Show labels every ~7 days
  const labelInterval = Math.max(1, Math.floor(dailyCounts.length / 5));

  return (
    <div className={theme.card.padded}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className={`text-sm font-semibold ${theme.text.heading} mb-1`}>Assessment Activity</h3>
          <p className={`text-xs ${theme.text.muted}`}>Last 30 days</p>
        </div>
        <div className="text-right">
          <p className={`text-lg font-bold ${theme.text.heading}`}>{totalAssessments}</p>
          <p className={`text-xs ${theme.text.muted}`}>Total</p>
        </div>
      </div>

      {totalAssessments === 0 ? (
        <div className="flex items-center justify-center h-36">
          <p className={`text-sm ${theme.text.muted}`}>No assessments in the last 30 days</p>
        </div>
      ) : (
        <>
          <div className="flex items-end gap-[3px]" style={{ height: `${BAR_HEIGHT_PX}px` }}>
            {dailyCounts.map((day) => {
              const barPx = day.count > 0
                ? Math.max(Math.round((day.count / maxCount) * BAR_HEIGHT_PX), MIN_BAR_PX)
                : MIN_BAR_PX;

              return (
                <div
                  key={day.date}
                  className="flex-1 flex flex-col items-center justify-end group relative"
                  style={{ height: `${BAR_HEIGHT_PX}px` }}
                >
                  {/* Tooltip */}
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 whitespace-nowrap pointer-events-none">
                    <div className="px-2 py-1 rounded-lg text-[10px] font-medium dark:bg-white/[0.12] bg-gray-800 dark:text-white text-white backdrop-blur-sm shadow-lg">
                      {formatDate(day.date)}: {day.count}
                    </div>
                  </div>

                  {/* Bar */}
                  <div
                    className={`w-full rounded-t-sm transition-all duration-500 ease-out ${
                      day.count > 0
                        ? 'dark:bg-sy-teal bg-teal-500 dark:group-hover:bg-sy-teal/80 group-hover:bg-teal-400'
                        : 'dark:bg-white/[0.06] bg-gray-100'
                    }`}
                    style={{ height: `${barPx}px` }}
                  />
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div className="flex mt-2">
            {dailyCounts.map((day, i) => (
              <div key={day.date} className="flex-1 text-center">
                {i % labelInterval === 0 && (
                  <span className={`text-[9px] ${theme.text.veryMuted}`}>
                    {formatDate(day.date)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
